/**
 * JsonViewer - Компонент для красивого отображения JSON данных
 */

import { CopyOutlined, SearchOutlined } from '@ant-design/icons'
import { Input, message, Tooltip } from 'antd'
import React, { useState } from 'react'

interface JsonViewerProps {
    data: any
    defaultExpandLevel?: number
    onSearch?: (searchTerm: string) => void
    showSearch?: boolean
}

export const JsonViewer: React.FC<JsonViewerProps> = ({
    data,
    defaultExpandLevel = 2,
    onSearch,
    showSearch = false,
}) => {
    const [collapsed, setCollapsed] = useState<Set<string>>(new Set())
    const [searchTerm, setSearchTerm] = useState('')

    const handleSearch = (value: string) => {
        setSearchTerm(value)
        if (onSearch) {
            onSearch(value)
        }
        // Если есть поисковый запрос, разворачиваем все элементы
        if (value) {
            setCollapsed(new Set())
        }
    }

    // Функция для проверки, содержит ли значение поисковый термин
    const matchesSearch = (value: any, key: string): boolean => {
        if (!searchTerm) return false

        const search = searchTerm.toLowerCase()

        // Проверяем ключ
        if (key.toLowerCase().includes(search)) return true

        // Проверяем значение
        if (value === null || value === undefined) return false

        if (typeof value === 'string') {
            return value.toLowerCase().includes(search)
        }

        if (typeof value === 'number' || typeof value === 'boolean') {
            return value.toString().toLowerCase().includes(search)
        }

        return false
    }

    // Проверяем, содержит ли объект или массив совпадения в глубину
    const containsSearchMatch = (value: any, key: string = ''): boolean => {
        if (!searchTerm) return false

        if (matchesSearch(value, key)) return true

        if (Array.isArray(value)) {
            return value.some((item, idx) => containsSearchMatch(item, `[${idx}]`))
        }

        if (typeof value === 'object' && value !== null) {
            return Object.keys(value).some((objKey) => containsSearchMatch(value[objKey], objKey))
        }

        return false
    }

    const highlightText = (text: string, isKey: boolean = false): React.ReactNode => {
        if (!searchTerm || !text) return text

        const parts = text.split(new RegExp(`(${searchTerm})`, 'gi'))
        return parts.map((part, idx) =>
            part.toLowerCase() === searchTerm.toLowerCase() ? (
                <span
                    key={idx}
                    style={{
                        backgroundColor: '#ffeb3b',
                        color: '#000',
                        fontWeight: 300,
                        padding: '1px 2px',
                        borderRadius: '2px',
                    }}
                >
                    {part}
                </span>
            ) : (
                part
            )
        )
    }

    const toggleCollapse = (path: string) => {
        setCollapsed((prev) => {
            const next = new Set(prev)
            if (next.has(path)) {
                next.delete(path)
            } else {
                next.add(path)
            }
            return next
        })
    }

    const copyValue = (value: any, e: React.MouseEvent) => {
        e.stopPropagation()
        const text = typeof value === 'string' ? value : JSON.stringify(value, null, 2)
        navigator.clipboard.writeText(text).then(() => {
            message.success('Скопійовано!')
        })
    }

    const renderValue = (
        value: any,
        key: string,
        path: string,
        level: number,
        isLastItem: boolean
    ): React.ReactNode => {
        const indent = level * 16
        const isCollapsed = collapsed.has(path)
        const shouldAutoCollapse = level >= defaultExpandLevel

        if (value === null) {
            return (
                <span>
                    <span style={{ color: '#808080', fontStyle: 'italic' }}>null</span>
                    {!isLastItem && <span style={{ color: '#666' }}>,</span>}
                </span>
            )
        }

        if (value === undefined) {
            return (
                <span>
                    <span style={{ color: '#808080', fontStyle: 'italic' }}>undefined</span>
                    {!isLastItem && <span style={{ color: '#666' }}>,</span>}
                </span>
            )
        }

        if (typeof value === 'boolean') {
            return (
                <span>
                    <span style={{ color: '#0000ff', fontWeight: 300 }}>{value.toString()}</span>
                    {!isLastItem && <span style={{ color: '#666' }}>,</span>}
                </span>
            )
        }

        if (typeof value === 'number') {
            return (
                <span>
                    <span style={{ color: '#098658', fontWeight: 500 }}>{value}</span>
                    {!isLastItem && <span style={{ color: '#666' }}>,</span>}
                </span>
            )
        }

        if (typeof value === 'string') {
            const isTruncated = value.length > 100
            const displayValue = isTruncated ? value.substring(0, 100) + '...' : value

            return (
                <span>
                    <span style={{ color: '#a31515' }}>
                        "{highlightText(displayValue)}"
                        {isTruncated && (
                            <Tooltip title="Натисніть, щоб скопіювати повний текст">
                                <CopyOutlined
                                    style={{
                                        marginLeft: 6,
                                        fontSize: 12,
                                        color: '#1890ff',
                                        cursor: 'pointer',
                                    }}
                                    onClick={(e) => copyValue(value, e)}
                                />
                            </Tooltip>
                        )}
                    </span>
                    {!isLastItem && <span style={{ color: '#666' }}>,</span>}
                </span>
            )
        }

        if (Array.isArray(value)) {
            const itemCount = value.length
            const isEmpty = itemCount === 0
            const currentCollapsed = isEmpty ? false : shouldAutoCollapse ? !collapsed.has(path) : isCollapsed

            if (isEmpty) {
                return (
                    <span>
                        <span style={{ color: '#666' }}>[]</span>
                        {!isLastItem && <span style={{ color: '#666' }}>,</span>}
                    </span>
                )
            }

            return (
                <div style={{ display: 'inline-block', width: '100%' }}>
                    <span
                        style={{
                            cursor: 'pointer',
                            userSelect: 'none',
                            color: '#0000ff',
                            marginRight: 6,
                            fontSize: 12,
                            display: 'inline-block',
                            width: 12,
                            textAlign: 'center',
                        }}
                        onClick={() => toggleCollapse(path)}
                    >
                        {currentCollapsed ? '▶' : '▼'}
                    </span>
                    <span style={{ color: '#666' }}>
                        [
                        <span style={{ color: '#999', fontSize: 12, marginLeft: 4 }}>
                            {itemCount} {itemCount === 1 ? 'item' : 'items'}
                        </span>
                    </span>
                    {!currentCollapsed && (
                        <>
                            {value.map((item, idx) => (
                                <div key={idx} style={{ marginLeft: indent + 16, lineHeight: 1.6 }}>
                                    <span style={{ color: '#999', marginRight: 8, minWidth: 20, display: 'inline-block' }}>
                                        {idx}:
                                    </span>
                                    {renderValue(item, `[${idx}]`, `${path}[${idx}]`, level + 1, idx === value.length - 1)}
                                </div>
                            ))}
                            <div style={{ marginLeft: indent }}>
                                <span style={{ color: '#666' }}>]</span>
                                {!isLastItem && <span style={{ color: '#666' }}>,</span>}
                            </div>
                        </>
                    )}
                    {currentCollapsed && (
                        <span>
                            <span style={{ color: '#666' }}> ]</span>
                            {!isLastItem && <span style={{ color: '#666' }}>,</span>}
                        </span>
                    )}
                </div>
            )
        }

        if (typeof value === 'object') {
            const keys = Object.keys(value)
            const itemCount = keys.length
            const isEmpty = itemCount === 0
            const currentCollapsed = isEmpty ? false : shouldAutoCollapse ? !collapsed.has(path) : isCollapsed

            if (isEmpty) {
                return (
                    <span>
                        <span style={{ color: '#666' }}>{'{}'}</span>
                        {!isLastItem && <span style={{ color: '#666' }}>,</span>}
                    </span>
                )
            }

            return (
                <div style={{ display: 'inline-block', width: '100%' }}>
                    <span
                        style={{
                            cursor: 'pointer',
                            userSelect: 'none',
                            color: '#0000ff',
                            marginRight: 6,
                            fontSize: 12,
                            display: 'inline-block',
                            width: 12,
                            textAlign: 'center',
                        }}
                        onClick={() => toggleCollapse(path)}
                    >
                        {currentCollapsed ? '▶' : '▼'}
                    </span>
                    <span style={{ color: '#666' }}>
                        {'{'}
                        <span style={{ color: '#999', fontSize: 12, marginLeft: 4 }}>
                            {itemCount} {itemCount === 1 ? 'key' : 'keys'}
                        </span>
                    </span>
                    {!currentCollapsed && (
                        <>
                            {keys.map((objKey, idx) => (
                                <div key={objKey} style={{ marginLeft: indent + 16, lineHeight: 1.6 }}>
                                    <Tooltip title="Натисніть, щоб скопіювати ключ">
                                        <span
                                            style={{
                                                color: '#001080',
                                                fontWeight: 500,
                                                cursor: 'pointer',
                                                backgroundColor: matchesSearch(value[objKey], objKey)
                                                    ? '#ffeb3b'
                                                    : 'transparent',
                                                padding: matchesSearch(value[objKey], objKey) ? '1px 3px' : 0,
                                                borderRadius: '2px',
                                            }}
                                            onClick={(e) => copyValue(objKey, e)}
                                        >
                                            "{highlightText(objKey)}"
                                        </span>
                                    </Tooltip>
                                    <span style={{ color: '#666' }}>: </span>
                                    {renderValue(
                                        value[objKey],
                                        objKey,
                                        `${path}.${objKey}`,
                                        level + 1,
                                        idx === keys.length - 1
                                    )}
                                </div>
                            ))}
                            <div style={{ marginLeft: indent }}>
                                <span style={{ color: '#666' }}>{'}'}</span>
                                {!isLastItem && <span style={{ color: '#666' }}>,</span>}
                            </div>
                        </>
                    )}
                    {currentCollapsed && (
                        <span>
                            <span style={{ color: '#666' }}> {'}'}</span>
                            {!isLastItem && <span style={{ color: '#666' }}>,</span>}
                        </span>
                    )}
                </div>
            )
        }

        return (
            <span>
                <span style={{ color: '#808080' }}>{String(value)}</span>
                {!isLastItem && <span style={{ color: '#666' }}>,</span>}
            </span>
        )
    }

    if (typeof data === 'object' && data !== null) {
        const isArray = Array.isArray(data)
        const items = isArray ? data : Object.keys(data)
        const isEmpty = items.length === 0

        return (
            <>
                {showSearch && (
                    <div style={{ marginBottom: 16 }}>
                        <Input
                            placeholder="Пошук по ключам і значенням..."
                            prefix={<SearchOutlined />}
                            value={searchTerm}
                            onChange={(e) => handleSearch(e.target.value)}
                            allowClear
                            style={{ width: '100%' }}
                        />
                    </div>
                )}
                <div
                    style={{
                        fontFamily: '"Fira Code", "JetBrains Mono", Monaco, Menlo, "Ubuntu Mono", monospace',
                        fontSize: '13px',
                        lineHeight: '1.6',
                        padding: '20px',
                        background: 'linear-gradient(to bottom, #fafafa 0%, #f5f5f5 100%)',
                        borderRadius: '6px',
                        overflow: 'auto',
                        maxHeight: '70vh',
                        border: '1px solid #e8e8e8',
                        boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.05)',
                    }}
                >
                {isArray ? (
                    <>
                        <div>
                            <span style={{ color: '#666' }}>
                                [
                                {!isEmpty && (
                                    <span style={{ color: '#999', fontSize: 12, marginLeft: 4 }}>
                                        {data.length} {data.length === 1 ? 'item' : 'items'}
                                    </span>
                                )}
                            </span>
                        </div>
                        {isEmpty ? (
                            <div style={{ marginLeft: 16, color: '#999', fontStyle: 'italic' }}>пусто</div>
                        ) : (
                            data.map((item: any, idx: number) => (
                                <div key={idx} style={{ marginLeft: 16, lineHeight: 1.6 }}>
                                    <span style={{ color: '#999', marginRight: 8, minWidth: 20, display: 'inline-block' }}>
                                        {idx}:
                                    </span>
                                    {renderValue(item, `[${idx}]`, `root[${idx}]`, 0, idx === data.length - 1)}
                                </div>
                            ))
                        )}
                        <div>
                            <span style={{ color: '#666' }}>{']'}</span>
                        </div>
                    </>
                ) : (
                    <>
                        <div>
                            <span style={{ color: '#666' }}>
                                {'{'}
                                {!isEmpty && (
                                    <span style={{ color: '#999', fontSize: 12, marginLeft: 4 }}>
                                        {items.length} {items.length === 1 ? 'key' : 'keys'}
                                    </span>
                                )}
                            </span>
                        </div>
                        {isEmpty ? (
                            <div style={{ marginLeft: 16, color: '#999', fontStyle: 'italic' }}>пусто</div>
                        ) : (
                            items.map((key: string, idx: number) => (
                                <div key={key} style={{ marginLeft: 16, lineHeight: 1.6 }}>
                                    <Tooltip title="Натисніть, щоб скопіювати ключ">
                                        <span
                                            style={{
                                                color: '#001080',
                                                fontWeight: 500,
                                                cursor: 'pointer',
                                                backgroundColor: matchesSearch(data[key], key) ? '#ffeb3b' : 'transparent',
                                                padding: matchesSearch(data[key], key) ? '1px 3px' : 0,
                                                borderRadius: '2px',
                                            }}
                                            onClick={(e) => copyValue(key, e)}
                                        >
                                            "{highlightText(key)}"
                                        </span>
                                    </Tooltip>
                                    <span style={{ color: '#666' }}>: </span>
                                    {renderValue(data[key], key, `root.${key}`, 0, idx === items.length - 1)}
                                </div>
                            ))
                        )}
                        <div>
                            <span style={{ color: '#666' }}>{'}'}</span>
                        </div>
                    </>
                )}
                </div>
            </>
        )
    }

    return (
        <>
            {showSearch && (
                <div style={{ marginBottom: 16 }}>
                    <Input
                        placeholder="Пошук по ключам і значенням..."
                        prefix={<SearchOutlined />}
                        value={searchTerm}
                        onChange={(e) => handleSearch(e.target.value)}
                        allowClear
                        style={{ width: '100%' }}
                    />
                </div>
            )}
            <div
                style={{
                    fontFamily: '"Fira Code", "JetBrains Mono", Monaco, Menlo, "Ubuntu Mono", monospace',
                    fontSize: '13px',
                    padding: '20px',
                    background: 'linear-gradient(to bottom, #fafafa 0%, #f5f5f5 100%)',
                    borderRadius: '6px',
                    border: '1px solid #e8e8e8',
                }}
            >
                {renderValue(data, 'root', 'root', 0, true)}
            </div>
        </>
    )
}
